import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { P, match } from 'ts-pattern';


import Button from 'lib/common/components/Button';
import { BUTTON_SIZES } from 'lib/common/components/Button/Button';
import Text from 'lib/common/components/Text';

import toast from '../../../../utils/toast';
import { useAIContext } from '../../Context';
import { getBedrockAgentForAction } from '../IQActionsMenu/constants/IQActionItems';
import { TIQDisplayMode } from '../constants/IQDisplayMode';
import { IQDisplayMode } from '../constants/IQDisplayMode';
import { IQResponseState } from '../constants/IQResponseState';
import IQResultActions from './components/IQResultActions/IQResultActions';
import IQResultBody from './components/IQResultBody/IQResultBody';

import styles from './iq-result.module.scss';

export default function IQResult({
  displayMode,
  hasFeedback = false,
  showAcceptForRewrites,
  isAnimationComplete,
}: {
  displayMode?: TIQDisplayMode;
  hasFeedback?: boolean;
  showAcceptForRewrites: boolean;
  isAnimationComplete: boolean;
}) {
  const {
    state: { response, requestState, isStreaming, replaceTextCallbackRef, selectedAction, streamingState },
    actions: { closeIQModal, closeActionMenu },
  } = useAIContext();

  const isRethinking = requestState === IQResponseState.RETHINKING;
  const bedrockAgent = getBedrockAgentForAction(selectedAction);
  const suggestedRewrite = match({ isStreaming, isRethinking, hasFeedback, bedrockAgent })
    .with({ isStreaming: true, isRethinking: false, hasFeedback: true }, () => null)
    .with({ isStreaming: true, isRethinking: false }, () => streamingState.rewrite ?? streamingState.output)
    .with({ bedrockAgent: P.nullish }, () => null)
    .otherwise(() => streamingState.rewrite ?? streamingState.output);

  if (!suggestedRewrite && !hasFeedback && !response) {
    return null;
  }

  const description = suggestedRewrite || 'No suggested rewrites';
  const disableAccept = !suggestedRewrite || isRethinking;

  const onAcceptSuggestion = () => {
    if (!suggestedRewrite || !replaceTextCallbackRef || !replaceTextCallbackRef.current) {
      return;
    }
    try {
      replaceTextCallbackRef.current(suggestedRewrite);

      if (displayMode === IQDisplayMode.STAND_ALONE) {
        closeIQModal();
        return;
      }

      closeActionMenu();
    } catch {
      toast('error', 'Unable to replace text, if this continues please contact support');
    }
  };

  const suggestedRewriteFooter = () => (
    <div className={styles['iq-result-footer']}>
      <IQResultActions textToCopy={description} />
      {showAcceptForRewrites && (
        <Button
          onClick={onAcceptSuggestion}
          className={styles['iq-result-footer__button']}
          icon={faCheck}
          size={BUTTON_SIZES.SMALL}
          disabled={disableAccept}
        >
          Accept
        </Button>
      )}
    </div>
  );

  const inlineSuggestion = () => (
    <div className={styles['iq-result-without-feedback']}>
      <Text type="body" className={styles['iq-result-without-feedback__description']}>
        {description}
      </Text>
      {suggestedRewriteFooter()}
    </div>
  );

  const standAloneSuggestion = () => {
    if (!isAnimationComplete) {
      return null;
    }

    if (!suggestedRewrite) {
      return inlineSuggestion();
    }

    return (
      <div className={styles['iq-result-with-feedback']}>
        <IQResultBody title="Suggested Rewrite" description={description} isThinking={isRethinking} />
        {suggestedRewriteFooter()}
      </div>
    );
  };

  return hasFeedback ? standAloneSuggestion() : inlineSuggestion();
}
